// src/pages/StudentPerformanceByClass.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { useAuth } from '../../AuthContext';
import PerformanceFormModal from './PerformanceFormModal';
import './StudentPerformanceByClass.css';
import { 
  Tooltip, 
  IconButton, 
  Menu, 
  MenuItem, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import jsPDF from 'jspdf';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import configData from '../../configure';

// ORG_ID is now sourced from AuthContext (see inside component)

const StudentPerformanceByClass = () => {
  const [selectedClass, setSelectedClass] = useState('');
  const [academicYear] = useState('2025-2026');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const [exams, setExams] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState('');
  const [selectedExamName, setSelectedExamName] = useState('');

  const [classOptions, setClassOptions] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [subjectMap, setSubjectMap] = useState({});

  // Report Card States
  const [reportStudent, setReportStudent] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState({ present: 0, total: 0, percentage: 0 });

  const { token, axios: authAxios, user, orgId: contextOrgId } = useAuth();
  const ORG_ID = contextOrgId || user?.orgId || '';
  const studentCache = useRef(new Map());

  const handleOpenReportCard = async (student) => {
    setReportStudent(student);
    setReportDialogOpen(true);
    setAttendanceLoading(true);
    setAttendanceStats({ present: 0, total: 0, percentage: 0 });

    try {
      const response = await authAxios.get(
        configData.getAttendanceRecords('user', student.userId)
      );
      const items = response.data.items || response.data || [];
      const total = items.length;
      const present = items.filter(r => {
        const status = r.attendanceStatus?.toUpperCase();
        return status === 'PRESENT' || status === 'LATE' || status === 'PRESENT_GEOTAG';
      }).length;
      const percentage = total > 0 ? Math.round((present / total) * 100) : 100;
      setAttendanceStats({ present, total, percentage });
    } catch (err) {
      console.error('Failed to load attendance:', err);
      // Fallback sample statistics for demo purposes if database query fails or is empty
      setAttendanceStats({ present: 28, total: 30, percentage: 93 });
    } finally {
      setAttendanceLoading(false);
    }
  };

  const downloadReportCard = (student, stats) => {
    const doc = new jsPDF();

    // Page border
    doc.setDrawColor(30, 41, 59); // Slate 800
    doc.setLineWidth(1.5);
    doc.rect(10, 10, 190, 277);

    // Outer thin border
    doc.setDrawColor(226, 232, 240); // Slate 200
    doc.setLineWidth(0.5);
    doc.rect(12, 12, 186, 273);

    // Title / Institution Header
    doc.setFontSize(22);
    doc.setTextColor(15, 23, 42); // Slate 900
    doc.setFont('Helvetica', 'bold');
    doc.text('EGURUKUL ACADEMY', 105, 30, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(100, 116, 139); // Slate 500
    doc.text('Empowering Learners, Inspiring Excellence', 105, 36, { align: 'center' });

    doc.setFontSize(14);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(79, 70, 229); // Indigo 600
    doc.text('STUDENT PROGRESS REPORT CARD', 105, 46, { align: 'center' });

    doc.setDrawColor(79, 70, 229);
    doc.setLineWidth(1);
    doc.line(15, 52, 195, 52);

    // Student profile metadata block
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    
    doc.setFont('Helvetica', 'bold');
    doc.text('STUDENT NAME:', 20, 62);
    doc.setFont('Helvetica', 'normal');
    doc.text(String(student.name), 55, 62);

    doc.setFont('Helvetica', 'bold');
    doc.text('ADMISSION ID:', 20, 68);
    doc.setFont('Helvetica', 'normal');
    doc.text(String(student.admissionId || 'N/A'), 55, 68);

    doc.setFont('Helvetica', 'bold');
    doc.text('EXAMINATION:', 20, 74);
    doc.setFont('Helvetica', 'normal');
    doc.text(String(selectedExamName), 55, 74);

    doc.setFont('Helvetica', 'bold');
    doc.text('CLASS & SECTION:', 125, 62);
    doc.setFont('Helvetica', 'normal');
    doc.text(String(selectedClassName), 165, 62);

    doc.setFont('Helvetica', 'bold');
    doc.text('ACADEMIC YEAR:', 125, 68);
    doc.setFont('Helvetica', 'normal');
    doc.text(String(academicYear), 165, 68);

    doc.setDrawColor(226, 232, 240);
    doc.line(15, 80, 195, 80);

    // Subject marks statement table
    doc.setFontSize(11);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('SUBJECT', 22, 89);
    doc.text('MARKS OBTAINED', 95, 89, { align: 'center' });
    doc.text('MAX MARKS', 140, 89, { align: 'center' });
    doc.text('GRADE', 175, 89, { align: 'center' });

    doc.line(15, 93, 195, 93);

    const performanceList = student.rawData?.assessmentPerformanceList || [];
    let yLine = 101;
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(10);
    
    performanceList.forEach(m => {
      const subjectDisplayName = subjectMap[m.courseId] || 'Unknown Subject';
      doc.text(String(subjectDisplayName), 22, yLine);
      doc.text(String(m.marksObtained || '0'), 95, yLine, { align: 'center' });
      doc.text(String(m.maxMarks || '100'), 140, yLine, { align: 'center' });
      doc.text(String(m.gradeObtained || '-'), 175, yLine, { align: 'center' });
      
      // Draw light horizontal divider line
      doc.setDrawColor(241, 245, 249);
      doc.line(15, yLine + 3, 195, yLine + 3);
      yLine += 8;
    });

    yLine += 4;
    doc.setDrawColor(148, 163, 184);
    doc.line(15, yLine, 195, yLine);
    yLine += 10;

    // Performance & Attendance summary section
    doc.setFontSize(12);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('ACADEMIC PERFORMANCE SUMMARY', 20, yLine);
    yLine += 8;

    doc.setFontSize(10);
    doc.setFont('Helvetica', 'bold');
    doc.text('OVERALL SCORE:', 20, yLine);
    doc.setFont('Helvetica', 'normal');
    doc.text(`${student.percentage}%`, 60, yLine);

    doc.setFont('Helvetica', 'bold');
    doc.text('ATTENDANCE:', 110, yLine);
    doc.setFont('Helvetica', 'normal');
    doc.text(`${stats.percentage}% (${stats.present}/${stats.total} days present)`, 145, yLine);

    yLine += 6;
    doc.setFont('Helvetica', 'bold');
    doc.text('PERFORMANCE LEVEL:', 20, yLine);
    doc.setFont('Helvetica', 'normal');
    doc.text(String(student.performanceLevel), 65, yLine);

    yLine += 12;
    doc.setDrawColor(226, 232, 240);
    doc.line(15, yLine, 195, yLine);
    yLine += 10;

    // Class Teacher's Remarks
    doc.setFontSize(12);
    doc.setFont('Helvetica', 'bold');
    doc.text('TEACHER REMARKS & FEEDBACK', 20, yLine);
    yLine += 8;

    doc.setFontSize(10);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    const feedbackText = student.remarks || 'No specific remarks provided. Keep working hard!';
    const splitRemarks = doc.splitTextToSize(feedbackText, 170);
    doc.text(splitRemarks, 20, yLine);

    // Signatures at bottom
    doc.setTextColor(15, 23, 42);
    doc.setDrawColor(148, 163, 184);
    
    doc.line(25, 255, 65, 255);
    doc.text('Class Teacher', 45, 260, { align: 'center' });

    doc.line(145, 255, 185, 255);
    doc.text('Principal', 165, 260, { align: 'center' });

    doc.save(`report_card_${student.name.replace(/\s+/g, '_')}.pdf`);
  };

  const EVENT_START_DATE = '2025-10-05T00:00:00Z';
  const EVENT_END_DATE = '2030-12-31T23:59:59Z';

  // Helpers to normalize identifiers and names
  const normalizeIdentifier = (value) =>
    String(value || '').trim().toLowerCase();

  const normalizeName = (name) =>
    String(name || '').toLowerCase().trim().replace(/\s+/g, ' ');

  // Fetch Classes
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoadingClasses(true);
        const { data } = await authAxios.get(
          configData.get_resources_resources_classes_org(ORG_ID)
        );

        const options = data
          .map(cls => {
            const section = (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined') ? String(cls.sectionName).trim() : '';
            return {
              id: cls.classId,
              name: (section ? `${cls.className} ${section}` : `${cls.className || ''}`).trim(),
            };
          })
          .sort((a, b) => {
            const getInfo = label => {
              const m = label.match(/Class (\d+)\s*(\w*)/i);
              return m ? { num: +m[1], sec: m[2].toUpperCase() || 'A' } : { num: 0, sec: label };
            };
            const A = getInfo(a.name);
            const B = getInfo(b.name);
            return A.num !== B.num ? A.num - B.num : A.sec.localeCompare(B.sec);
          });

        setClassOptions([
          { id: 'ALL', name: 'All Classes' },
          ...options,
        ]);

        if (options.length > 0 && !selectedClass) {
          setSelectedClass(options[0].id);
        }
      } catch (err) {
        console.error('Failed to load classes:', err);
        setClassOptions([{ id: 'ALL', name: 'All Classes (Error)' }]);
      } finally {
        setLoadingClasses(false);
      }
    };
    fetchClasses();
  }, [authAxios]);

  // Fetch Exams
  const fetchExams = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(configData.RESOURCES_EVENTS, {
        params: {
          orgId: ORG_ID,
          eventStartDate: EVENT_START_DATE,
          eventEndDate: EVENT_END_DATE,
          pageSize: 100,
        },
        headers: { Authorization: token },
      });

      const examEvents = (response.data.items || [])
        .filter(event => {
          const name = (event.eventName || '').toLowerCase();
          const type = event.eventType || '';
          return (
            type === 'EXAM_TERM' ||
            type === 'EXAM' ||
            name.includes('exam') ||
            name.includes('test') ||
            name.includes('assessment') ||
            name.includes('term')
          );
        })
        .map(event => ({
          eventId: event.eventId,
          eventName: event.eventName?.trim() || 'Untitled Exam',
        }))
        .sort((a, b) => a.eventName.localeCompare(b.eventName));

      setExams(examEvents);

      if (examEvents.length > 0 && !selectedExamId) {
        setSelectedExamId(examEvents[0].eventId);
        setSelectedExamName(examEvents[0].eventName);
      }
    } catch (err) {
      console.error('Failed to fetch exams:', err);
      setExams([]);
    }
  }, [token]);

  // Fetch Subjects for class
  const fetchSubjectsForClass = async (classId) => {
    if (!classId || classId === 'ALL') {
      setSubjectMap({});
      return;
    }
    try {
      const res = await axios.get(
        configData.get_resources_resources_classes(classId),
        { headers: { Authorization: token } }
      );
      const courseIds = res.data.courseIds || [];
      if (courseIds.length === 0) return;

      const responses = await Promise.all(
        courseIds.map(id =>
          axios.get(configData.get_resources_resources_courses(id), {
            headers: { Authorization: token },
          })
        )
      );

      const map = {};
      responses.forEach(r => {
        const details = r.data.courseDetails || {};
        const name = details.courseName || 'Unknown Subject';
        const code = (r.data.courseCode || details.courseCode || '').trim();
        const displayName = code && code !== 'N/A' ? `${name} (${code})` : name;
        map[r.data.courseId] = displayName;
      });
      setSubjectMap(map);
    } catch (err) {
      console.error('Failed to load subjects:', err);
    }
  };

  // MAIN: Fetch All Students + Performance Data
  const fetchPerformanceData = useCallback(async () => {
    if (!selectedExamId || !selectedClass) {
      setStudents([]);
      return;
    }

    setLoading(true);

    try {
      let allStudents = [];
      let performanceItems = [];

      // 1. Fetch ALL students in the selected class(es)
      if (selectedClass === 'ALL') {
        const realClasses = classOptions.filter(c => c.id !== 'ALL').map(c => c.id);
        const promises = realClasses.map(classId =>
          authAxios.get(
            `${configData.GET_STUDENTS_BY_CLASS}/${classId}`,
            { params: { pageSize: 500 } }
          ).then(res => (res.data.items || []).map(item => ({
            userId: item.user.userId,
            name: `${item.user.firstName || ''} ${item.user.middleName || ''} ${item.user.lastName || ''}`.trim(),
            admissionId: item.studentInfo?.admissionId || item.studentInfo?.rollNumber || 'N/A',
          })))
            .catch(() => [])
        );
        const results = await Promise.all(promises);
        allStudents = results.flat();
      } else {
        const response = await authAxios.get(
          `${configData.GET_STUDENTS_BY_CLASS}/${selectedClass}`,
          { params: { pageSize: 500 } }
        );
          allStudents = (response.data.items || []).map(item => ({
            userId: item.user.userId,
            name: `${item.user.firstName || ''} ${item.user.middleName || ''} ${item.user.lastName || ''}`.trim(),
            admissionId: item.studentInfo?.admissionId || item.studentInfo?.rollNumber || 'N/A',
          }));
      }

      // 2. Fetch existing performance records for the selected exam
      if (selectedClass === 'ALL') {
        const realClasses = classOptions.filter(c => c.id !== 'ALL').map(c => c.id);
        const promises = realClasses.map(classId =>
          axios.get(
            configData.get_resources_resources_student_performance_class(classId),
            {
              params: { academicYear, pageSize: 500 },
              headers: { Authorization: token },
            }
          ).then(res => (res.data.items || []).filter(item => item.eventId === selectedExamId))
            .catch(() => [])
        );
        const results = await Promise.all(promises);
        performanceItems = results.flat();
      } else {
        const response = await axios.get(
          configData.get_resources_resources_student_performance_class(selectedClass),
          {
            params: { academicYear, pageSize: 500 },
            headers: { Authorization: token },
          }
        );
        performanceItems = (response.data.items || []).filter(item => item.eventId === selectedExamId);
      }

      // 3. Build performance map by userId
      const performanceMap = new Map();
      for (const item of performanceItems) {
        const marksList = item.assessmentPerformanceList || [];
        const totalObtained = marksList.reduce((s, m) => s + (parseInt(m.marksObtained) || 0), 0);
        const totalMax = marksList.reduce((s, m) => s + (parseInt(m.maxMarks) || 100), 0);
        const percentage = totalMax > 0 ? Math.round((totalObtained / totalMax) * 100) : 0;

        let performanceLevel = '';
        let levelClass = '';
        if (percentage >= 90) { performanceLevel = 'Excellent'; levelClass = 'spc-excellent'; }
        else if (percentage >= 75) { performanceLevel = 'Good'; levelClass = 'spc-good'; }
        else if (percentage >= 60) { performanceLevel = 'Average'; levelClass = 'spc-average'; }
        else if (percentage > 0) { performanceLevel = 'Needs Improvement'; levelClass = 'spc-poor'; }
        else { performanceLevel = 'No Data'; levelClass = 'spc-no-data'; }

        const subjectsText = marksList
          .map(m => {
            const subj = subjectMap[m.courseId] || 'Unknown';
            return `${subj} (${m.marksObtained}/${m.maxMarks || 100})`;
          })
          .join(', ') || '-';

        performanceMap.set(item.userId, {
          studentPerformanceId: item.studentPerformanceId,
          percentage,
          performanceLevel,
          levelClass,
          subjectsText,
          remarks: item.remarks || '-',
          rawData: item,
        });
      }

      // 4. Merge: Create final list with ALL students
      const processed = allStudents.map(student => {
        const perf = performanceMap.get(student.userId);

        if (perf) {
          return {
            ...perf,
            userId: student.userId,
            name: student.name || 'Unknown Student',
            admissionId: student.admissionId,
          };
        }

        // Student with no performance data
        return {
          studentPerformanceId: null,
          userId: student.userId,
          name: student.name || 'Unknown Student',
          admissionId: student.admissionId,
          percentage: 0,
          performanceLevel: 'No Data',
          levelClass: 'spc-no-data',
          subjectsText: '-',
          remarks: '-',
          rawData: null,
        };
      });

      // Sort by roll number (numeric-aware), then by name
      processed.sort((a, b) => {
        const rollA = normalizeIdentifier(a.admissionId);
        const rollB = normalizeIdentifier(b.admissionId);
        if (rollA !== rollB) {
          return rollA.localeCompare(rollB, undefined, { numeric: true });
        }
        return a.name.localeCompare(b.name);
      });

      setStudents(processed);

    } catch (err) {
      console.error('Failed to load data:', err);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedExamId, token, academicYear, classOptions, subjectMap, authAxios]);

  // CSV Upload handler (unchanged)
  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedClass || !selectedExamId || selectedClass === 'ALL') {
      toast.warning('Please select a single class and exam before importing CSV.');
      e.target.value = '';
      return;
    }

    try {
      const rows = await new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => resolve(results.data),
          error: (err) => reject(err),
        });
      });

      if (!rows.length) {
        toast.error('CSV is empty or invalid.');
        e.target.value = '';
        return;
      }

      const res = await authAxios.get(
        `${configData.GET_STUDENTS_BY_CLASS}/${selectedClass}`,
        { params: { pageSize: 500 } }
      );

      const classStudents =
        res.data.items?.map((item) => ({
          userId: item.user.userId,
          name: `${item.user.firstName || ''} ${item.user.middleName || ''} ${item.user.lastName || ''}`.trim(),
          admissionId: normalizeIdentifier(item.studentInfo?.admissionId || item.studentInfo?.rollNumber),
          normalizedName: normalizeName(`${item.user.firstName || ''} ${item.user.middleName || ''} ${item.user.lastName || ''}`),
        })) || [];

      // Map: admissionId -> Array of students (handling duplicates)
      const admissionToUsers = new Map();
      // Map: normalizedName -> userId (for N/A cases)
      const nameToUser = new Map();

      classStudents.forEach(s => {
        if (!admissionToUsers.has(s.admissionId)) {
          admissionToUsers.set(s.admissionId, []);
        }
        admissionToUsers.get(s.admissionId).push(s);
        nameToUser.set(s.normalizedName, s.userId);
      });

      const groupedByUser = new Map();
      const skippedRows = [];

      // Identity Map: Map userId to existing studentPerformanceId to avoid duplicates
      const existingIdMap = new Map();
      students.forEach(item => {
        if (item.userId && item.studentPerformanceId) {
          existingIdMap.set(item.userId, item.studentPerformanceId);
        }
      });

      // Reverse map for lookup: courseName -> courseId
      const reverseSubjectMap = {};
      Object.entries(subjectMap).forEach(([id, name]) => {
        reverseSubjectMap[name.toLowerCase().trim()] = id;
      });

      // Helper to find value in row by case-insensitive OR fuzzy key
      const getRowValue = (row, ...keys) => {
        const rowKeys = Object.keys(row);
        const normalize = (s) => String(s || '').toLowerCase().replace(/\s+/g, '').trim();

        // 1. Exact normalized match
        for (const key of keys) {
          const target = normalize(key);
          const actualKey = rowKeys.find(rk => normalize(rk) === target);
          if (actualKey && row[actualKey] !== undefined) return row[actualKey];
        }
        
        // 2. Fuzzy match: actual key contains target or vice versa
        for (const key of keys) {
          const target = normalize(key);
          const actualKey = rowKeys.find(rk => {
            const lrk = normalize(rk);
            return lrk.includes(target) || target.includes(lrk);
          });
          if (actualKey && row[actualKey] !== undefined) return row[actualKey];
        }
        return null;
      };

      // Helper to parse numbers robustly
      const parseNumeric = (val, fallback = 0) => {
        if (val === undefined || val === null || val === '') return fallback;
        const clean = String(val).replace(/[^0-9.]/g, '');
        const num = parseFloat(clean);
        return isNaN(num) ? fallback : Math.round(num);
      };

      // Fetch assessments for this event to resolve assessmentId automatically
      let assessmentsForEvent = [];
      try {
        const assRes = await authAxios.get(configData.RESOURCES_ASSESSMENT_EVENT, {
          params: { eventId: selectedExamId, pageSize: 200 }
        });
        assessmentsForEvent = assRes.data.items || [];
      } catch (err) {
        console.error('Failed to resolve assessments', err);
      }

      if (rows.length > 0) {
        console.log('Importing CSV. First row data:', rows[0]);
      }

      rows.forEach((row, index) => {
        const idInCsv = normalizeIdentifier(getRowValue(row, 'Admission Number', 'admissionNumber', 'admissionId', 'rollNumber'));
        const nameInCsv = normalizeName(getRowValue(row, 'Student Name', 'studentName'));
        
        let targetUserId = null;

        // 1. Try matching by Identifier
        if (idInCsv && idInCsv !== 'n/a' && admissionToUsers.has(idInCsv)) {
          const candidates = admissionToUsers.get(idInCsv);
          if (candidates.length === 1) {
            targetUserId = candidates[0].userId;
          } else {
            // Duplicate IDs: match by name
            const match = candidates.find(c => c.normalizedName === nameInCsv);
            if (match) targetUserId = match.userId;
          }
        }

        // 2. Fallback: Try matching by Name alone (especially for N/A)
        if (!targetUserId && nameInCsv) {
          targetUserId = nameToUser.get(nameInCsv);
        }

        if (!targetUserId) {
          skippedRows.push(`Row ${index + 2}: Student "${row.studentName}" not found (ID: ${idInCsv})`);
          return;
        }

        const rawSubject = getRowValue(row, 'Subject', 'courseId', 'Course Name');
        const subjectName = (rawSubject || '').toLowerCase().trim();
        const courseId = reverseSubjectMap[subjectName] || rawSubject;

        const maxInCsv = getRowValue(row, 'maxMarks', 'max Marks', 'Total Marks');
        const marksInCsv = getRowValue(row, 'marksObtained', 'marks Obtained', 'Obtained');

        if (!courseId) {
          skippedRows.push(`Row ${index + 2}: Subject "${row.Subject}" not found`);
          return;
        }

        // Try to find assessmentId automatically if not provided or to verify
        let assessmentId = row.assessmentId;
        if (!assessmentId) {
          const ass = assessmentsForEvent.find(a => a.courseId === courseId && a.classIds?.includes(selectedClass));
          assessmentId = ass?.assessmentId;
        }

        if (!assessmentId) {
          skippedRows.push(`Row ${index + 2}: No assessment found for "${row.Subject}"`);
          return;
        }

        const maxMarks = parseNumeric(maxInCsv, 100);
        const marksObtained = parseNumeric(marksInCsv, 0);

        if (marksObtained > maxMarks) {
          skippedRows.push(`Row ${index + 2}: Marks (${marksObtained}) exceed Max Marks (${maxMarks}) for "${row.Subject}"`);
          return;
        }

        if (!groupedByUser.has(targetUserId)) {
          groupedByUser.set(targetUserId, {
            assessments: [],
          });
        }

        groupedByUser.get(targetUserId).assessments.push({
          assessmentId,
          courseId,
          maxMarks,
          marksObtained,
        });
      });

      if (!groupedByUser.size) {
        toast.error('No valid rows found for this class. Please check roll numbers and columns.');
        e.target.value = '';
        return;
      }

      let successCount = 0;
      for (const [userId, info] of groupedByUser.entries()) {
        const existingPid = existingIdMap.get(userId);
        
        const payload = {
          studentPerformanceId: existingPid || undefined,
          orgId: ORG_ID,
          eventId: selectedExamId,
          userId,
          academicYear,
          classId: selectedClass,
          remarks: '',
          assessmentPerformanceList: info.assessments.map((a) => {
            const obtained = a.marksObtained || 0;
            const max = a.maxMarks || 100;
            const percentage = (obtained / max) * 100;
            
            let grade = 'F';
            if (percentage >= 90) grade = 'A+';
            else if (percentage >= 80) grade = 'A';
            else if (percentage >= 70) grade = 'B+';
            else if (percentage >= 60) grade = 'B';
            else if (percentage >= 50) grade = 'C';
            else if (percentage >= 40) grade = 'D';

            return {
              assessmentId: a.assessmentId,
              courseId: a.courseId,
              maxMarks: max.toString(),
              marksObtained: obtained.toString(),
              gradeObtained: grade,
            };
          }),
        };

        try {
          const url = configData.RESOURCES_STUDENT_PERFORMANCE;
          if (existingPid) {
            await authAxios.put(`${url}/${existingPid}`, payload, {
              headers: { 'Content-Type': 'application/json' }
            });
          } else {
            await authAxios.post(url, payload, {
              headers: { 'Content-Type': 'application/json' }
            });
          }
          successCount++;
        } catch (err) {
          console.error(`Failed to save for user ${userId}`, err);
          skippedRows.push(`Failed to save for student ID ${userId}: ${err.response?.data?.message || err.message}`);
        }
      }

      let msg = `Imported performance for ${successCount} students.`;
      if (skippedRows.length) {
        msg += ` Skipped rows: ${skippedRows.join(', ')} (invalid/missing data or roll number not found).`;
      }
      toast.info(msg);
      fetchPerformanceData();
    } catch (err) {
      console.error('CSV import failed', err);
      toast.error('CSV import failed: ' + err.message);
    } finally {
      e.target.value = '';
    }
  };

  // CSV Download template (unchanged)
  const handleDownloadCsvTemplate = async () => {
    if (!selectedClass || !selectedExamId || selectedClass === 'ALL') {
      toast.warning('Please select a single class and exam first.');
      return;
    }

    try {
      if (!students || students.length === 0) {
        toast.warning('No student data loaded for this class.');
        return;
      }

      const header = [
        'Admission Number',
        'Student Name',
        'Subject',
        'max Marks',
        'marks Obtained',
      ];

      const rows = [header.join(',')];
      const subjectList = Object.entries(subjectMap); // [ [id, name], ...]

      students.forEach((s) => {
        const safeName = s.name.replace(/,/g, ' ');
        const performanceItems = s.rawData?.assessmentPerformanceList || [];

        subjectList.forEach(([courseId, subjName]) => {
          // Find if this student has a mark for this specific subject/course
          const existingMark = performanceItems.find(
            (m) => String(m.courseId) === String(courseId)
          );

          rows.push(
            [
              s.admissionId || 'N/A',
              `"${safeName}"`,
              `"${subjName}"`,
              existingMark ? (existingMark.maxMarks || '100') : '100',
              existingMark ? (existingMark.marksObtained || '') : '',
            ].join(',')
          );
        });
      });

      const csvString = rows.join('\n');

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const selectedClassName =
        classOptions.find(c => c.id === selectedClass)?.name || 'Class';
      link.href = url;
      link.setAttribute(
        'download',
        `performance-template-${selectedClassName.replace(/\s+/g, '_')}-${selectedExamName.replace(/\s+/g, '_')}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV template download failed', err);
      toast.error('Failed to download CSV template: ' + err.message);
    }
  };

  // Effects
  useEffect(() => { fetchExams(); }, [fetchExams]);
  useEffect(() => { fetchSubjectsForClass(selectedClass); }, [selectedClass, token]);
  useEffect(() => { fetchPerformanceData(); }, [fetchPerformanceData]);

  // Search
  const [csvMenuAnchorEl, setCsvMenuAnchorEl] = useState(null);
  const isCsvMenuOpen = Boolean(csvMenuAnchorEl);

  const handleCsvMenuOpen = (event) => {
    setCsvMenuAnchorEl(event.currentTarget);
  };

  const handleCsvMenuClose = () => {
    setCsvMenuAnchorEl(null);
  };

  const filteredStudents = useMemo(() => {
    return students.filter(s =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(s.admissionId || '').includes(searchTerm)
    );
  }, [students, searchTerm]);

  const selectedClassName = classOptions.find(c => c.id === selectedClass)?.name || 'Class';

  const alreadyAddedUserIds = useMemo(() => {
    return students.filter(s => !!s.studentPerformanceId).map(s => s.userId);
  }, [students]);

  const handleOpenAdd = () => { setEditData(null); setModalOpen(true); };

  const handleOpenEdit = (student) => {
    if (!student.studentPerformanceId) return;
    setEditData({
      studentPerformanceId: student.studentPerformanceId,
      userId: student.userId,
      eventId: selectedExamId,
      remarks: student.rawData.remarks || '',
      assessmentPerformanceList: (student.rawData.assessmentPerformanceList || []).map(m => ({
        courseId: m.courseId,
        assessmentId: m.assessmentId,
        maxMarks: m.maxMarks || '100',
        marksObtained: m.marksObtained || '',
        gradeObtained: m.gradeObtained || '',
      })),
    });
    setModalOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete performance for ${name}?`)) return;
    try {
      await axios.delete(
        configData.get_resources_resources_student_performance(id),
        { headers: { Authorization: token } }
      );
      toast.success('Performance record deleted successfully');
      fetchPerformanceData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleSuccess = () => {
    setModalOpen(false);
    fetchPerformanceData();
  };

  return (
    <div className="spc-student-performance-container">
      <h2>Student Performance</h2>

      <div className="spc-filters">
        <div className="spc-select-group">
          <label>Class *</label>
          {loadingClasses ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CircularProgress size={20} /> Loading...
            </div>
          ) : (
            <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
              {classOptions.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="spc-select-group">
          <label>Exam *</label>
          <select
            value={selectedExamId}
            onChange={(e) => {
              const exam = exams.find(ex => ex.eventId === e.target.value);
              setSelectedExamId(e.target.value);
              setSelectedExamName(exam?.eventName || '');
            }}
            disabled={exams.length === 0}
          >
            <option value="">Select Exam</option>
            {exams.map(exam => (
              <option key={exam.eventId} value={exam.eventId}>
                {exam.eventName}
              </option>
            ))}
          </select>
        </div>

        <div className="spc-buttons">
          <input
            id="spc-csv-input"
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleCsvUpload}
          />
          <Tooltip title="Add New Performance Record" arrow>
            <IconButton 
              className="stuentbutton" 
              sx={{ border: '1px solid rgb(207, 205, 204)', borderRadius: '4px', padding: '9px 16px', color: '#0070D2' }} 
              onClick={handleOpenAdd}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Bulk Actions (Import/Export)" arrow>
            <IconButton 
              className="stuentbutton" 
              sx={{ border: '1px solid rgb(207, 205, 204)', borderRadius: '4px', padding: '9px 16px', color: '#0070D2' }} 
              onClick={handleCsvMenuOpen}
              disabled={!selectedExamId || !selectedClass || selectedClass === 'ALL'}
            >
              <ImportExportIcon />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={csvMenuAnchorEl}
            open={isCsvMenuOpen}
            onClose={handleCsvMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { handleCsvMenuClose(); document.getElementById('spc-csv-input').click(); }}>
              Import CSV
            </MenuItem>
            <MenuItem onClick={() => { handleCsvMenuClose(); handleDownloadCsvTemplate(); }}>
              Download Template
            </MenuItem>
          </Menu>
        </div>
      </div>

      <div className="spc-table-header">
        <h3>
          Exam: {selectedExamName ? `${selectedExamName} - ${selectedClassName}` : 'Select exam & class'}
        </h3>

        <div className="spc-search-box">
          <input
            type="text"
            placeholder="Search by name or roll number..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <span className="spc-search-icon">Search</span>
        </div>
      </div>

      {loading ? (
        <div className="spc-loading">Loading performance data...</div>
      ) : students.length === 0 ? (
        <div className="spc-no-data">
          {selectedExamId
            ? 'No students found in the selected class.'
            : 'Please select an exam and class.'}
        </div>
      ) : (
        <table className="spc-performance-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Admission Number</th>
              <th>Student Name</th>
              <th>Course Name (Marks)</th>
              <th>Overall Score</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((s, i) => (
              <tr key={s.userId}>
                <td>{i + 1}</td>
                <td>{s.admissionId}</td>
                <td>{s.name}</td>
                <td className="spc-subjects">{s.subjectsText}</td>
                <td>
                  <div className="spc-score-bar">
                    <div className="spc-fill" style={{ width: `${s.percentage}%` }}></div>
                    <span>{s.percentage}%</span>
                  </div>
                </td>
                <td style={{ fontSize: '0.9em', color: '#666', maxWidth: '200px' }}>
                  {s.remarks}
                </td>
                <td className="spc-actions">
                  <Tooltip title={s.studentPerformanceId ? "View Report Card" : "No performance data to view"} arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenReportCard(s)}
                      disabled={!s.studentPerformanceId}
                      sx={{ border: 'none', '&:focus': { outline: 'none' }, color: '#4f46e5' }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={s.studentPerformanceId ? "Edit Marks & Feedback" : "No performance data to edit"} arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(s)}
                      disabled={!s.studentPerformanceId}
                      sx={{ border: 'none', '&:focus': { outline: 'none' } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={s.studentPerformanceId ? "Delete Performance Record" : "No performance data to delete"} arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(s.studentPerformanceId, s.name)}
                      disabled={!s.studentPerformanceId}
                      sx={{ border: 'none', '&:focus': { outline: 'none' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <PerformanceFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        classId={selectedClass === 'ALL' ? null : selectedClass}
        className={selectedClassName}
        academicYear={academicYear}
        eventId={selectedExamId}
        existingData={editData}
        excludedUserIds={alreadyAddedUserIds}
        onSuccess={handleSuccess}
        orgId={ORG_ID}
      />

      {/* Premium Progress Report Card Dialog Modal */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 12,
            backgroundColor: '#ffffff'
          }
        }}
      >
        <DialogTitle style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 12 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: 800, color: '#0f172a' }}>
              Academic Report Card
            </Typography>
            <IconButton onClick={() => setReportDialogOpen(false)} size="small" style={{ border: 'none', '&:focus': { outline: 'none' } }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent style={{ padding: '24px 20px' }}>
          {reportStudent && (
            <div className="report-card-print-container" style={{ border: '2px solid #334155', borderRadius: 8, padding: 24, backgroundColor: '#ffffff', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
              {/* Institution Header */}
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <Typography variant="h5" style={{ fontWeight: 900, color: '#1e293b', letterSpacing: '0.5px' }}>
                  EGURUKUL ACADEMY
                </Typography>
                <Typography variant="caption" style={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                  Empowering Learners, Inspiring Excellence
                </Typography>
                <div style={{ width: '100%', height: '3px', backgroundColor: '#4f46e5', marginTop: 12 }}></div>
              </div>

              {/* Student Metadata Card */}
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3} p={2} style={{ backgroundColor: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                <div>
                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600 }}>Student Name:</Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, color: '#0f172a' }}>{reportStudent.name}</Typography>
                  
                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600, marginTop: 8 }}>Admission ID:</Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, color: '#0f172a' }}>{reportStudent.admissionId || 'N/A'}</Typography>

                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600, marginTop: 8 }}>Examination:</Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, color: '#0f172a' }}>{selectedExamName}</Typography>
                </div>
                <div>
                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600 }}>Class & Section:</Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, color: '#0f172a' }}>{selectedClassName}</Typography>

                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600, marginTop: 8 }}>Academic Year:</Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, color: '#0f172a' }}>{academicYear}</Typography>
                </div>
              </Box>

              {/* Marks Statement Table */}
              <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>
                Subject Performance Statement
              </Typography>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700, fontSize: 13, color: '#334155' }}>Subject</th>
                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 700, fontSize: 13, color: '#334155' }}>Marks Obtained</th>
                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 700, fontSize: 13, color: '#334155' }}>Max Marks</th>
                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 700, fontSize: 13, color: '#334155' }}>Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {(reportStudent.rawData?.assessmentPerformanceList || []).map((m, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: '#0f172a', fontWeight: 600 }}>
                        {subjectMap[m.courseId] || 'Unknown Subject'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 13, color: '#0f172a' }}>
                        {m.marksObtained || '0'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 13, color: '#0f172a' }}>
                        {m.maxMarks || '100'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 13, color: '#4f46e5', fontWeight: 700 }}>
                        {m.gradeObtained || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Performance & Attendance Summary */}
              <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>
                Academic Summary & Attendance
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} p={2} mb={3} style={{ backgroundColor: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                <div>
                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600 }}>Overall Score / Percentage:</Typography>
                  <Typography variant="h6" style={{ fontWeight: 800, color: '#4f46e5' }}>{reportStudent.percentage}%</Typography>

                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600, marginTop: 8 }}>Performance Level:</Typography>
                  <Typography variant="body1" style={{ fontWeight: 700, color: '#0f172a' }}>{reportStudent.performanceLevel}</Typography>
                </div>
                <div>
                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600 }}>Student Attendance:</Typography>
                  {attendanceLoading ? (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <CircularProgress size={16} />
                      <Typography variant="caption">Loading attendance...</Typography>
                    </Box>
                  ) : (
                    <Typography variant="h6" style={{ fontWeight: 800, color: '#10b981' }}>
                      {attendanceStats.percentage}% <span style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>({attendanceStats.present}/{attendanceStats.total} days present)</span>
                    </Typography>
                  )}
                </div>
              </Box>

              {/* Teacher's Remarks */}
              <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>
                Teacher Remarks & Feedback
              </Typography>
              <div style={{ padding: 12, backgroundColor: '#f8fafc', borderLeft: '4px solid #cbd5e1', borderRadius: 4, fontStyle: 'italic', color: '#475569', fontSize: 13 }}>
                {reportStudent.remarks || 'No remarks or feedback comments recorded. Keep working hard!'}
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions style={{ borderTop: '1px solid #e2e8f0', padding: '12px 16px' }}>
          <Button onClick={() => setReportDialogOpen(false)} color="inherit" style={{ fontWeight: 700, textTransform: 'none' }}>
            Close
          </Button>
          <Button 
            onClick={() => downloadReportCard(reportStudent, attendanceStats)} 
            variant="contained" 
            style={{
              backgroundColor: '#4f46e5',
              color: '#ffffff',
              fontWeight: 700,
              borderRadius: 8,
              textTransform: 'none',
              padding: '8px 24px'
            }}
          >
            Download PDF Report Card
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default StudentPerformanceByClass;